import React, { Component, memo } from 'react';
import PropTypes from 'prop-types';
import T from 'i18n-react';
import { camelCase } from 'lodash';
import baseStyles from './styles';
import Grid from '@material-ui/core/Grid';
import clsx from 'clsx';
import Skeleton from '@material-ui/lab/Skeleton';
import Box from '@material-ui/core/Box';
import FacilityImagesButton from './FacilityImagesButton';
import { NEARBY_HOTEL } from '../variables';
import FacilityDetail from './FacilityDetail';
import Typography from '@material-ui/core/Typography';
import Divider from '@material-ui/core/Divider';
import Amenities from './Amenities/Amenities';
import Meals from './Meals';
import NearbyPlaces from './NearbyPlaces';
import Linkify from '../components/Linkify';

export const styles = (theme) => ({
  root: {
    margin: theme.spacing(3, 0),
  },
  box: {
    ...baseStyles.box,
  },
  grid: {
    color: theme.palette.text.primary,
  },
  divider: {
    margin: theme.spacing(3, 0),
  },
  itemLabel: {
    fontSize: 16,
    fontWeight: 'bold',
    marginBottom: theme.spacing(2),
  },
  itemContent: {
    fontSize: 14,
    minHeight: 14 * 1.5, // l line height at least,
    whiteSpace: 'pre-wrap',
  },
  accommodation: {
    fontSize: theme.typography.pxToRem(14),
  },
});

const Content = memo(({ classes, label, loaded, value, isLast = false }) => (
  <>
    <Grid item xs={12} className={clsx(classes.grid, classes.itemLabel)}>
      {label}
    </Grid>
    <Grid
      item
      xs={12}
      className={clsx(classes.grid, classes.itemContent, 'text-break-all')}
    >
      {loaded ? (
        <Linkify>{value}</Linkify>
      ) : (
        <Skeleton variant="text" width="100%" height="100%" />
      )}
    </Grid>
    {!isLast && (
      <Grid item xs={12} className={classes.divider}>
        <Divider />
      </Grid>
    )}
  </>
));

class ContentSummary extends Component {
  dataRenderer(key, value) {
    return value;
  }

  gridContainerRenderer(key, isLast = false) {
    const data = this.props[key] || '';
    return (
      <Grid container alignItems={data ? 'baseline' : 'center'}>
        {this.gridRenderer(key, isLast)}
      </Grid>
    );
  }

  gridRenderer(key, isLast = false) {
    const { classes, loaded } = this.props;

    return (
      <Content
        classes={classes}
        loaded={loaded}
        label={T.translate(`plan.${key}`)}
        value={this.dataRenderer(key, this.props[camelCase(key)])}
        isLast={isLast}
      />
    );
  }

  facilityRenderer() {
    const {
      classes,
      loaded,
      accommodation,
      facility: {
        kind = '',
        roomType = '',
        otherKind = '',
        otherRoomType = '',
        address = '',
        hotelName,
        hotelUrl,
        images = [],
      } = {},
    } = this.props;
    const hasOtherKind = kind === 'other_kind' && otherKind;
    const hasOtherRoomType = roomType === 'other_room_type' && otherRoomType;

    return (
      <Grid container alignItems={'baseline'}>
        <Grid item xs={12} className={clsx(classes.grid, classes.itemLabel)}>
          {T.translate('plan.facility.label')}
        </Grid>
        <Grid
          item
          xs={12}
          className={clsx(classes.grid, classes.itemContent, 'text-break-all')}
          container
          justify="space-between"
        >
          {loaded ? (
            <>
              {otherKind || kind ? (
                <>
                  <Box pb={2}>
                    <span>
                      {hasOtherKind
                        ? otherKind
                        : T.translate(`plan.facility.kind.${kind}`)}
                    </span>
                    {(otherRoomType || roomType) && (
                      <span>
                        （
                        {hasOtherRoomType
                          ? otherRoomType
                          : T.translate(`plan.facility.roomType.${roomType}`)}
                        ）
                      </span>
                    )}
                  </Box>
                  {kind === NEARBY_HOTEL && (
                    <FacilityDetail
                      label={T.translate('plan.facility.labels.hotelName')}
                      content={hotelName}
                    />
                  )}
                  {kind === NEARBY_HOTEL && (
                    <FacilityDetail
                      label={T.translate('plan.facility.labels.hotelUrl')}
                      content={hotelUrl}
                      isLink={true}
                    />
                  )}
                  {!!address && (
                    <FacilityDetail
                      label={T.translate('plan.facility.labels.address')}
                      content={address}
                    />
                  )}
                  {!!accommodation && (
                    <FacilityDetail
                      label={T.translate('plan.facility.labels.remark')}
                      content={accommodation}
                    />
                  )}
                  {kind !== NEARBY_HOTEL && 0 < images.length && (
                    <Grid item xs={12}>
                      <FacilityImagesButton images={images}>
                        {T.translate('seeX', {
                          x: T.translate('plan.facility.images.title'),
                        })}
                      </FacilityImagesButton>
                    </Grid>
                  )}
                </>
              ) : (
                <Typography className={classes.accommodation}>
                  {accommodation}
                </Typography>
              )}
            </>
          ) : (
            <Skeleton variant="text" width="100%" height="100%" />
          )}
        </Grid>
        <Grid item xs={12} className={classes.divider}>
          <Divider />
        </Grid>
      </Grid>
    );
  }

  amenityRenderer() {
    const { classes, amenity, amenities, isAmenityUnavailable } = this.props;
    return (
      <Grid container alignItems={'baseline'}>
        <Grid item xs={12} className={clsx(classes.grid, classes.itemLabel)}>
          {T.translate('plan.amenity')}
        </Grid>
        <Grid
          item
          xs={12}
          className={clsx(classes.grid, classes.itemContent, 'text-break-all')}
          container
          justify="space-between"
        >
          <Amenities {...{ amenity, amenities, isAmenityUnavailable }} />
        </Grid>
        <Grid item xs={12} className={classes.divider}>
          <Divider />
        </Grid>
      </Grid>
    );
  }

  mealsRenderer() {
    const { classes, loaded, meals, mealGrouping } = this.props;
    return (
      <Grid container alignItems={'baseline'}>
        <Grid item xs={12} className={clsx(classes.grid, classes.itemLabel)}>
          {T.translate('plan.meal')}
        </Grid>
        <Grid
          item
          xs={12}
          className={clsx(classes.grid, classes.itemContent, 'text-break-all')}
          container
          justify="space-between"
        >
          {loaded ? (
            <Meals meals={meals} mealGrouping={mealGrouping} />
          ) : (
            <Skeleton variant="text" width="100%" height="100%" />
          )}
        </Grid>
        <Grid item xs={12} className={classes.divider}>
          <Divider />
        </Grid>
      </Grid>
    );
  }

  nearbyPlaceRenderer() {
    const { classes, nearbyPlace, nearbyPlaces, isNearbyPlaceUnavailable } =
      this.props;
    return (
      <Grid container alignItems={'baseline'}>
        <Grid item xs={12} className={clsx(classes.grid, classes.itemLabel)}>
          {T.translate('plan.nearbyPlace')}
        </Grid>
        <Grid
          item
          xs={12}
          className={clsx(classes.grid, classes.itemContent, 'text-break-all')}
          container
          justify="space-between"
        >
          <NearbyPlaces
            {...{ nearbyPlace, nearbyPlaces, isNearbyPlaceUnavailable }}
          />
        </Grid>
        <Grid item xs={12} className={classes.divider}>
          <Divider />
        </Grid>
      </Grid>
    );
  }

  notesRenderer() {
    const { classes, loaded, notes, comment } = this.props;
    const isCommentPresent = comment != null && comment !== '';
    return (
      <Content
        classes={classes}
        loaded={loaded}
        label={T.translate(`plan.notes`)}
        value={
          <>
            {this.dataRenderer('notes', notes)}
            {/* おてつたび先のメッセージの互換性を保つために登録済みの場合は備考の項目に統合して表示する。 */}
            {isCommentPresent && (
              <>
                <br />
                <br />
                {this.dataRenderer('comment', comment)}
              </>
            )}
          </>
        }
        isLast
      />
    );
  }

  render() {
    const { classes } = this.props;

    return (
      <div className={classes.root}>
        <div className={classes.box}>
          {this.gridContainerRenderer('content')}
          {this.gridContainerRenderer('recruitment_background')}
          {this.gridContainerRenderer('benefit')}
          {this.mealsRenderer()}
          {this.facilityRenderer()}
          {this.amenityRenderer()}
          {this.gridContainerRenderer('belonging')}
          {this.nearbyPlaceRenderer()}
          {this.notesRenderer()}
        </div>
      </div>
    );
  }
}

ContentSummary.propTypes = {
  loaded: PropTypes.bool,
  content: PropTypes.string,
  accommodation: PropTypes.string,
  amenities: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string,
      description: PropTypes.string,
    }),
  ),
  isAmenityUnavailable: PropTypes.bool,
  meals: PropTypes.arrayOf(
    PropTypes.shape({
      timing: PropTypes.string,
      kind: PropTypes.string,
      otherKind: PropTypes.string,
      description: PropTypes.string,
    }),
  ),
  benefit: PropTypes.string,
  belonging: PropTypes.string,
  facility: PropTypes.shape({
    id: PropTypes.number,
    kind: PropTypes.string,
    roomType: PropTypes.string,
    otherKind: PropTypes.string,
    otherRoomType: PropTypes.string,
    hotelName: PropTypes.string,
    hotelUrl: PropTypes.string,
    images: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.number,
        displayOrder: PropTypes.number,
        url: PropTypes.string,
        kind: PropTypes.string,
        description: PropTypes.string,
      }),
    ),
  }),
  notes: PropTypes.string,
  comment: PropTypes.string,
};

export default ContentSummary;
